<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h4 class="mb-0">
        Список цепочек
      </h4>
      <b-button
        size="sm"
        variant="outline-primary"
        @click="load"
      >
        <feather-icon icon="RefreshCwIcon" /> Обновить
      </b-button>
    </div>
    <p class="text-muted">
      Реестр опубликованных процессов и того, кому они выданы. «Выдать цепочку» даёт роли или пользователю
      право запускать процесс и, при необходимости, переопределяет исполнителей этапов для их запусков.
    </p>

    <b-table
      responsive
      small
      hover
      :busy="loading"
      :fields="fields"
      :items="chains"
      :per-page="perPage"
      :current-page="currentPage"
      show-empty
      empty-text="Нет опубликованных процессов"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner small class="align-middle mr-50" /> Загрузка…
        </div>
      </template>
      <template #cell(name)="data">
        <span class="font-weight-bolder">{{ data.item.name }}</span>
        <b-badge variant="light-secondary" class="ml-50">v{{ data.item.version }}</b-badge>
      </template>
      <template #cell(active_instances)="data">
        <b-badge :variant="data.value > 0 ? 'light-primary' : 'light-secondary'">
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(grants)="data">
        <div
          v-if="(data.item.grants || []).length"
          class="d-flex flex-wrap"
        >
          <b-badge
            v-for="g in data.item.grants"
            :key="g.id"
            :variant="g.can_start ? 'light-success' : 'light-secondary'"
            class="mr-50 mb-25 d-flex align-items-center"
          >
            {{ g.role_name || g.user_sid || '—' }}
            <feather-icon
              icon="XIcon"
              class="ml-50 cursor-pointer"
              @click="revoke(g.id)"
            />
          </b-badge>
        </div>
        <span
          v-else
          class="text-muted"
        >не выдана</span>
      </template>
      <template #cell(actions)="data">
        <b-button
          size="sm"
          variant="success"
          @click="openGrant(data.item)"
        >
          <feather-icon icon="UserPlusIcon" /> Выдать
        </b-button>
      </template>
    </b-table>
    <b-pagination
      v-if="chains.length > perPage"
      v-model="currentPage"
      :total-rows="chains.length"
      :per-page="perPage"
      align="center"
      size="sm"
      class="mt-1"
    />

    <b-modal
      v-model="grant.show"
      title="Выдать цепочку"
      centered
      hide-footer
    >
      <p class="mb-1">
        Процесс: <b>{{ grant.proc && grant.proc.name }}</b>
      </p>
      <b-form-group label="Кому выдать">
        <b-form-radio-group
          v-model="grant.target"
          :options="[{ value: 'role', text: 'Роли' }, { value: 'user', text: 'Пользователю (sid)' }]"
          size="sm"
        />
      </b-form-group>
      <b-form-group
        v-if="grant.target === 'role'"
        label="Роль"
      >
        <b-form-select
          v-model.number="grant.roleId"
          size="sm"
          :options="roleOptions"
        />
      </b-form-group>
      <b-form-group
        v-else
        label="User sid"
      >
        <b-form-input
          v-model="grant.userSid"
          size="sm"
          placeholder="usersid пользователя"
        />
      </b-form-group>
      <b-form-checkbox
        v-model="grant.canStart"
        class="mb-1"
      >
        Может запускать процесс
      </b-form-checkbox>
      <b-form-group label="Переопределение исполнителей этапов (JSON, необязательно)">
        <b-form-textarea
          v-model="grant.defaultAssignees"
          rows="3"
          size="sm"
          placeholder='{ "12": "role(manager)" }'
        />
        <small class="text-muted">Формат: id этапа → выражение исполнителей. Применится к запускам по этой выдаче.</small>
      </b-form-group>
      <div class="d-flex justify-content-end">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="grant.show = false"
        >
          Отмена
        </b-button>
        <b-button
          variant="success"
          :disabled="grant.busy || !grantValid"
          @click="submitGrant"
        >
          Выдать
        </b-button>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BButton, BBadge, BSpinner, BModal, BPagination,
  BFormGroup, BFormSelect, BFormInput, BFormTextarea, BFormCheckbox, BFormRadioGroup,
} from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'

export default {
  name: 'CbsBpmChains',
  components: {
    BCard, BTable, BButton, BBadge, BSpinner, BModal, BPagination,
    BFormGroup, BFormSelect, BFormInput, BFormTextarea, BFormCheckbox, BFormRadioGroup,
  },
  data() {
    return {
      loading: false,
      currentPage: 1,
      perPage: 10,
      chains: [],
      roles: [],
      fields: [
        { key: 'name', label: 'Процесс' },
        { key: 'active_instances', label: 'Активных' },
        { key: 'grants', label: 'Выдана' },
        { key: 'actions', label: '' },
      ],
      grant: {
        show: false,
        busy: false,
        proc: null,
        target: 'role',
        roleId: null,
        userSid: '',
        canStart: true,
        defaultAssignees: '',
      },
    }
  },
  computed: {
    roleOptions() {
      return [{ value: null, text: '— выберите роль —' }].concat(this.roles.map(r => ({ value: r.id, text: r.name })))
    },
    grantValid() {
      return this.grant.target === 'role' ? !!this.grant.roleId : !!(this.grant.userSid && this.grant.userSid.trim())
    },
  },
  created() {
    useCubus.setPageTitle('Список цепочек')
    this.load()
    this.loadRoles()
  },
  methods: {
    api(method, param) {
      return useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method, param, mode: 'sync' },
      }).then(r => {
        if (r.data.error) { useCubus.toastError(this, r.data.error); return null }
        return r.data.result
      }).catch(e => { useCubus.toastError(this, e); return null })
    },
    load() {
      this.loading = true
      this.api('bpm_chains', {}).then(r => { this.loading = false; if (r) this.chains = r.chains || [] })
    },
    loadRoles() {
      this.api('bpm_roles', {}).then(r => { if (r) this.roles = r.roles || [] })
    },
    openGrant(proc) {
      this.grant = {
        show: true, busy: false, proc, target: 'role', roleId: null, userSid: '', canStart: true, defaultAssignees: '',
      }
    },
    submitGrant() {
      if (!this.grantValid) return
      this.grant.busy = true
      const param = {
        process_def_id: this.grant.proc.id,
        can_start: this.grant.canStart,
        default_assignees_json: this.grant.defaultAssignees || '',
      }
      if (this.grant.target === 'role') param.role_id = this.grant.roleId
      else param.user_sid = this.grant.userSid.trim()
      this.api('bpm_chain_grant', param).then(r => {
        this.grant.busy = false
        if (r) { useCubus.toastMessage(this, 'Цепочка выдана'); this.grant.show = false; this.load() }
      })
    },
    revoke(grantId) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Отозвать выдачу?')) return
      this.api('bpm_chain_revoke', { id: grantId }).then(r => { if (r) this.load() })
    },
  },
}
</script>
